/* Header layouts (sections/header.liquid → snippets/header-*.liquid). One controller per page; the theme
   editor re-renders the header when a setting changes, so everything is read again on shopify:section:load.
   The header root carries data-header, its SHAPE in data-sticky and the founder's Scrolling setting in data-scroll.
   Shape: nav   — the rows marked data-scroll-away scroll off; the rest is the bar (Masthead's menu row)
          over  — floats over the first section on the homepage (Over the Photograph), clear until scrolled
          any other value — the whole header is the bar
   Scrolling (same in every layout): stay — the bar stays on screen; tuck — it scrolls off with the page like any
          other part and slides back on the first scroll up (.is-pinned), out again when reading down; away — it
          scrolls off with the page.
   The section wrapper gets .hd-wrap, [data-sticky]=shape, [data-scroll], and while scrolling .is-scrolled /
   .is-hidden; the header root gets .is-solid for "over". Menus are the theme's <details class="nav-disclosure"> (voisson.js closes the
   others and handles Escape); here a pointer that rests on a menu opens it and leaving closes it. [data-rotate]
   cycles its .rot-item children (the brand lines) and pauses on hover and focus. */
(() => {
  /* One controller per page: if the editor runs this file again, the first one re-reads the header instead. */
  if (window.__voissonHeader) { window.__voissonHeader.init(); return; }
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fine = matchMedia('(hover: hover) and (pointer: fine)');
  const S = { hd: null, wrap: null, shape: 'bar', scroll: 'stay', lastY: 0, ticking: false, rot: 0 };

  const measure = () => {
    if (!S.hd) return;
    let away = 0;
    S.hd.querySelectorAll('[data-scroll-away]').forEach((el) => { away += el.offsetHeight; });
    S.wrap.style.setProperty('--hd-away', away + 'px');
    S.wrap.style.setProperty('--hd-height', S.hd.offsetHeight + 'px');
    document.documentElement.style.setProperty('--hd-height', S.hd.offsetHeight + 'px');
    /* What stays on screen while scrolling, for sticky things below the header (product details). */
    if (S.scroll === 'away' && S.hd.classList.contains('hd--original')) document.documentElement.style.removeProperty('--hd-pinned');
    else if (S.scroll === 'away') document.documentElement.style.setProperty('--hd-pinned', '0px');
    else document.documentElement.style.setProperty('--hd-pinned', Math.max(0, S.hd.offsetHeight - (S.shape === 'nav' ? away : 0)) + 'px');
    S.away = away;
    /* Where the header sits in the page when it is not brought back (over the photo: the very top). */
    if (!S.wrap.classList.contains('is-pinned')) S.top = S.shape === 'over' ? 0 : Math.max(0, S.wrap.getBoundingClientRect().top + scrollY);
  };
  /* Over the photo the bar turns solid only when it must: keyboard focus inside, or the wide panel open. */
  const needsSolid = () => !!(S.hd.querySelector(':focus-visible') || S.hd.querySelector('details[open] > [data-wide]'));
  const busy = () => !!(S.hd.querySelector('details[open]') || document.querySelector('dialog[open]') || S.hd.querySelector(':focus-visible'));
  /* hold(): a jump the page makes itself (a colour tap) must not bring the header back on its way up. It lasts until
     the scrolling has been still for 150 ms (2 s at most); a brought-back header slides out for it. */
  let holdEnd = 0, holdQuiet = 0, holdTimer = 0;
  const held = () => performance.now() < Math.min(holdEnd, holdQuiet);
  const hold = () => {
    const now = performance.now(); holdEnd = now + 2000; holdQuiet = now + 300;
    if (S.wrap && S.scroll === 'tuck' && S.wrap.classList.contains('is-pinned')) S.wrap.classList.add('is-hidden');
  };
  addEventListener('scroll', () => { if (performance.now() < holdEnd) holdQuiet = performance.now() + 150; }, { passive: true });
  /* What of the header stays on screen over the page while scrolling (for jumps that must land below it). */
  const room = () => (!S.hd || S.scroll !== 'stay') ? 0 : Math.max(0, S.hd.offsetHeight - (S.shape === 'nav' ? (S.away || 0) : 0));
  const onScroll = () => {
    S.ticking = false;
    if (!S.hd) return;
    const y = Math.max(0, scrollY), dy = y - S.lastY;
    S.lastY = y;
    if (S.scroll === 'tuck') {
      /* In the page until it has scrolled fully out of view; then only a scroll up brings it back. */
      const home = S.top || 0, gone = y > home + S.hd.offsetHeight;
      const back = S.wrap.classList.contains('is-pinned');
      if (y <= home) S.wrap.classList.remove('is-pinned', 'is-hidden');
      else if (back && busy()) S.wrap.classList.remove('is-hidden');
      else if (dy < -5 && (back || gone) && !held()) { S.wrap.classList.add('is-pinned'); S.wrap.classList.remove('is-hidden'); }
      else if (dy > 5 && back) S.wrap.classList.add('is-hidden');
      const shown = S.wrap.classList.contains('is-pinned');
      S.wrap.classList.toggle('is-scrolled', shown);
      if (S.shape === 'over') S.hd.classList.toggle('is-solid', shown || needsSolid());
      return;
    }
    const threshold = S.shape === 'nav' ? Math.max(8, S.away || 0) : S.shape === 'over' ? 40 : 8;
    const pinned = S.scroll !== 'away' && y > threshold;
    S.wrap.classList.toggle('is-scrolled', pinned);
    if (S.shape === 'over') S.hd.classList.toggle('is-solid', pinned || needsSolid());
    S.wrap.classList.remove('is-hidden');
  };
  addEventListener('scroll', () => { if (!S.ticking) { S.ticking = true; requestAnimationFrame(onScroll); } }, { passive: true });
  addEventListener('resize', () => { measure(); onScroll(); });

  const hover = () => {
    if (S.hd.classList.contains('hd--original')) return; /* Original keeps its click-to-open menus */
    S.hd.querySelectorAll('details.nav-disclosure').forEach((d) => {
      if (d._hv) return;
      d._hv = true;
      let tIn, tOut;
      d.addEventListener('mouseenter', () => {
        if (!fine.matches) return;
        clearTimeout(tOut);
        const other = S.hd.querySelector('details.nav-disclosure[open]');
        tIn = setTimeout(() => { if (other && other !== d) other.open = false; if (!d.open) { d.open = true; d._hoverAt = Date.now(); } }, other ? 0 : 120);
      });
      /* The click that usually follows a hover-open must not close the menu the pointer just opened. */
      const sum = d.querySelector(':scope > summary');
      if (sum) sum.addEventListener('click', (e) => { if (d.open && d._hoverAt && Date.now() - d._hoverAt < 700) { e.preventDefault(); d._hoverAt = 0; } });
      /* Tabbing out of an open menu closes it; a click on its blank space (no new focus) does not. */
      d.addEventListener('focusout', (e) => { if (d.open && e.relatedTarget && !d.contains(e.relatedTarget)) d.open = false; });
      d.addEventListener('mouseleave', () => {
        if (!fine.matches) return;
        clearTimeout(tIn);
        tOut = setTimeout(() => { d.open = false; }, 240);
      });
    });
  };

  const rotator = () => {
    clearInterval(S.rot);
    const r = S.hd.querySelector('[data-rotate]');
    if (!r) return;
    const items = [...r.querySelectorAll('.rot-item')].filter((x) => x.textContent.trim());
    if (!items.length) return;
    let i = 0, paused = false, turns = 0;
    items.forEach((x, k) => { x.classList.toggle('is-on', k === 0); x.classList.remove('is-out'); if (k) x.setAttribute('aria-hidden', 'true'); else x.removeAttribute('aria-hidden'); });
    if (reduce || items.length < 2) return;
    r.addEventListener('mouseenter', () => { paused = true; });
    r.addEventListener('mouseleave', () => { paused = false; });
    /* Keyboard users reach the header before the lines change under them; the lines stop after two rounds. */
    S.hd.addEventListener('focusin', () => { paused = true; });
    S.hd.addEventListener('focusout', () => { paused = false; });
    S.rot = setInterval(() => {
      if (paused || document.hidden) return;
      if (i === items.length - 1 && ++turns >= 2) { clearInterval(S.rot); if (items.length > 1) { items[i].classList.remove('is-on'); items[i].setAttribute('aria-hidden', 'true'); items[0].classList.add('is-on'); items[0].removeAttribute('aria-hidden'); } return; }
      const cur = items[i]; i = (i + 1) % items.length; const nx = items[i];
      cur.classList.add('is-out'); cur.classList.remove('is-on'); cur.setAttribute('aria-hidden', 'true');
      nx.classList.remove('is-out'); nx.classList.add('is-on'); nx.removeAttribute('aria-hidden');
      setTimeout(() => cur.classList.remove('is-out'), 650);
    }, 4200);
  };

  /* L9 + a G5-style turn in the two O letters. Starts with the confirmed bag moment,
     never on submit. One short sequence, no added wait or idle work. */
  const motionPreference = matchMedia('(prefers-reduced-motion: reduce)');
  const activeMotion = new Set(), motionByElement = new WeakMap();
  const motion = (el, frames, duration, delay = 0) => {
    if (!el || !el.animate || motionPreference.matches || document.hidden) return;
    const previous = motionByElement.get(el); if (previous) previous.cancel();
    const a = el.animate(frames, { duration, delay, easing: 'cubic-bezier(.16,1,.3,1)' });
    motionByElement.set(el, a); activeMotion.add(a);
    const clear = () => { activeMotion.delete(a); if (motionByElement.get(el) === a) motionByElement.delete(el); };
    a.finished.then(clear, clear);
  };
  const cancelMotion = () => { activeMotion.forEach(a => a.cancel()); activeMotion.clear(); };
  motionPreference.addEventListener('change', e => { if (e.matches) cancelMotion(); });
  document.addEventListener('visibilitychange', () => { if (document.hidden) cancelMotion(); });
  const inView = el => { const b = el.getBoundingClientRect(); return b.width > 0 && b.height > 0 && b.bottom > 0 && b.top < innerHeight && getComputedStyle(el).visibility !== 'hidden'; };
  const celebrate = bag => {
    if (motionPreference.matches || document.hidden) return;
    const scope = bag?.closest('.bag-float') || bag?.closest('[data-header]');
    if (!scope) return;
    scope.querySelectorAll('.voisson-logo').forEach(logo => {
      if (!inView(logo)) return;
      logo.querySelectorAll('.logo-letter').forEach((letter, i) => {
        const turn = i === 1 || i === 5;
        motion(letter, [{ transform: 'scale(1)' }, { transform: turn ? 'scale(1.12) scaleX(-1)' : 'scale(1.12)', offset: .35 }, { transform: 'scale(1)' }], 310, i * 30);
      });
    });
  };
  const menuState = open => {
    document.querySelectorAll('[data-open-dialog="menu-drawer"]').forEach(button => {
      button.classList.toggle('is-menu-open', open); button.setAttribute('aria-expanded', String(open));
    });
  };
  const dialogOpened = (dialog, opener) => {
    opener.setAttribute('aria-expanded', 'true');
    if (dialog.id === 'locale-dialog') {
      [opener, dialog].forEach(scope => scope.querySelectorAll('.globe-meridian').forEach(el => motion(el, [{ transform: 'scaleX(1)' }, { transform: 'scaleX(-1)', offset: .45 }, { transform: 'scaleX(1)' }], 260)));
    }
    if (dialog.id === 'menu-drawer') {
      menuState(true);
      const button = dialog.querySelector('[data-menu-close]');
      // Line spacing differs by icon set (6 in Original, 7 in Pandora's drawing; snippets/icon.liquid --menu-shift)
      const icon = button?.querySelector('svg'), shift = (icon && parseFloat(getComputedStyle(icon).getPropertyValue('--menu-shift'))) || 6;
      motion(button?.querySelector('.menu-line--top'), [{ transform: 'none' }, { transform: `translateY(${shift}px) rotate(45deg)` }], 220);
      motion(button?.querySelector('.menu-line--bottom'), [{ transform: 'none' }, { transform: `translateY(${-shift}px) rotate(-45deg)` }], 220);
      motion(button?.querySelector('.menu-line--mid'), [{ opacity: 1 }, { opacity: 0 }], 140);
    }
  };
  document.addEventListener('close', e => {
    if (e.target.id === 'menu-drawer') menuState(false);
    document.querySelectorAll('[data-open-dialog]').forEach(button => { if (button.dataset.openDialog === e.target.id) button.setAttribute('aria-expanded', 'false'); });
  }, true);

  const init = () => {
    S.hd = document.querySelector('[data-header]');
    if (!S.hd) return;
    S.wrap = S.hd.closest('.shopify-section') || S.hd.parentElement;
    S.shape = S.hd.dataset.sticky === 'nav' ? 'nav' : S.hd.dataset.sticky === 'over' ? 'over' : 'bar';
    S.scroll = ['stay', 'tuck', 'away'].includes(S.hd.dataset.scroll) ? S.hd.dataset.scroll : 'stay';
    S.wrap.classList.add('hd-wrap');
    S.wrap.dataset.sticky = S.shape;
    S.wrap.dataset.scroll = S.scroll;
    S.wrap.classList.remove('is-hidden', 'is-pinned');
    if (S.shape === 'over') {
      S.hd.addEventListener('focusin', onScroll);
      S.hd.addEventListener('focusout', () => setTimeout(onScroll, 0));
      S.hd.addEventListener('toggle', onScroll, true);
    } else {
      S.hd.addEventListener('focusin', () => S.wrap.classList.remove('is-hidden'));
    }
    measure(); hover(); rotator(); onScroll();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
  };
  /* Selecting the header in the theme editor brings it back if it was hidden on scroll. */
  document.addEventListener('shopify:section:select', (e) => { if (S.hd && e.target && e.target.contains(S.hd)) S.wrap.classList.remove('is-hidden'); });
  document.addEventListener('shopify:section:load', (e) => { if (e.target && e.target.querySelector && e.target.querySelector('[data-header]')) init(); });
  document.addEventListener('toggle', (e) => { if (S.hd && S.hd.contains(e.target)) S.wrap.classList.remove('is-hidden'); }, true);
  window.__voissonHeader = { init, measure, celebrate, dialogOpened, hold, room };
  init();
})();
